import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { usePagedResource } from "../api/usePagedResource";
import { useLiveCalls } from "../api/useLiveCalls";
import { DataTable } from "../components/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import type { Call, CallDirection, CallStatus } from "../api/types";
import { Badge, Button, Card, PageHeader, inputClass } from "../components/ui";

// Lista de llamadas con filtros.
//
// El refresco es por polling y **con el intervalo acotado** (fase-2 §9): sin
// tope, una pestaña abierta toda la tarde sobre un tenant con tráfico es una
// consulta por segundo contra la base por cada persona que tenga la vista
// abierta. Se refresca cada cinco segundos y solo mientras hay llamadas vivas;
// cuando todas han terminado, no hay nada que refrescar.
// Con llamadas en curso se mira segundo a segundo; en reposo basta con que
// una llamada nueva aparezca en unos segundos.
const POLL_INTERVAL_MS = 5000;
const POLL_IDLE_MS = 15000;
// Con el canal en vivo abierto el sondeo pasa a ser red de seguridad: tapa lo
// que se pudiera haber perdido, sin ser quien trae las novedades.
const POLL_RED_MS = 60000;

type Filters = {
  status: CallStatus | "";
  direction: CallDirection | "";
  from: string;
  to: string;
};

const EMPTY_FILTERS: Filters = { status: "", direction: "", from: "", to: "" };

export function Calls() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

  // El query se arma desde los filtros aplicados, no desde el formulario: así
  // escribir en una fecha no dispara una consulta por cada tecla.
  const query = buildQuery(filters);
  const lista = usePagedResource<Call>(`/api/v1/calls${query}`);
  const calls = lista.data;

  // El polling solo corre si hay algo que pueda cambiar.
  //
  // Refrescar recarga **la página en la que estás**, no te devuelve a la
  // primera. Y eso es correcto aquí por cómo pagina la API: una página se
  // define como «ids menores que este cursor», así que las llamadas nuevas
  // aparecen en la primera página y no desplazan a las demás. Con paginación
  // por `OFFSET` cada llamada nueva correría una fila de la página 2 a la 3, y
  // una de ellas se vería dos veces.
  const hasLiveCalls = (calls ?? []).some((call) => isLive(call.status));

  // El canal en vivo trae los cambios según pasan. Lo que hace al recibirlos
  // es recargar la página, no parchear la fila a mano: el evento trae el
  // estado de la llamada pero no su sitio en la lista —que depende de los
  // filtros y del orden—, y una lista remendada se desincroniza en cuanto el
  // filtro no coincide con lo que llegó.
  //
  // Se agrupa en una ventana corta porque una llamada que entra y contesta
  // dispara varios eventos seguidos, y recargar por cada uno sería peor que
  // sondear.
  const recargaPendiente = useRef<number | null>(null);
  const { estado: canal, desfase } = useLiveCalls(
    useCallback(() => {
      if (recargaPendiente.current !== null) return;
      recargaPendiente.current = window.setTimeout(() => {
        recargaPendiente.current = null;
        void lista.reload(true);
      }, 400);
    }, [lista]),
  );
  useEffect(
    () => () => {
      if (recargaPendiente.current !== null) window.clearTimeout(recargaPendiente.current);
    },
    [],
  );

  // Cuando el servidor dice que se perdió más de lo que puede reenviar, la
  // lista se recarga entera: enseñar un trozo sería peor que recargar.
  useEffect(() => {
    if (desfase > 0) void lista.reload(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desfase]);

  // El sondeo no desaparece: es lo que sigue funcionando cuando el canal no
  // está. Mientras hay canal se espacia, porque ya no es quien trae las
  // novedades sino la red de seguridad.
  //
  // **También sin llamadas vivas.** Antes el refresco dependía de que ya
  // hubiera una en la página cargada, así que una lista en reposo no se
  // actualizaba nunca: la llamada que empezaba mientras mirabas no aparecía
  // hasta recargar, que es justo cuando hace falta que aparezca.
  useInterval(
    () => void lista.reload(true),
    canal === "en-vivo"
      ? POLL_RED_MS
      : hasLiveCalls
        ? POLL_INTERVAL_MS
        : POLL_IDLE_MS,
  );

  const navigate = useNavigate();

  const columnas = useMemo<ColumnDef<Call, any>[]>(
    () => [
      {
        id: "id",
        accessorKey: "id",
        header: "Llamada",
        meta: { label: "Llamada" },
        cell: ({ row }) => (
          <Link
            to={`/calls/${row.original.id}`}
            // El enlace sigue estando aunque la fila entera sea clicable:
            // es lo que permite abrir la llamada en otra pestaña.
            onClick={(e) => e.stopPropagation()}
            className="font-mono text-xs text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200"
          >
            {row.original.id}
          </Link>
        ),
      },
      {
        id: "direction",
        accessorKey: "direction",
        header: "Dirección",
        meta: { label: "Dirección", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">
            {row.original.direction === "outbound" ? "Saliente" : "Entrante"}
          </span>
        ),
      },
      {
        id: "from",
        accessorKey: "from",
        header: "De",
        meta: { label: "De" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">{row.original.from}</span>
        ),
      },
      {
        id: "to",
        accessorKey: "to",
        header: "A",
        meta: { label: "A" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">{row.original.to}</span>
        ),
      },
      {
        id: "status",
        accessorKey: "status",
        header: "Estado",
        meta: { label: "Estado" },
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "answered_seconds",
        accessorKey: "answered_seconds",
        header: "Duración",
        meta: { label: "Duración", align: "right", width: "7rem" },
        cell: ({ row }) => (
          <span className="text-slate-600">
            {row.original.answered_seconds > 0 ? `${row.original.answered_seconds} s` : "—"}
          </span>
        ),
      },
      {
        id: "queued_at",
        accessorKey: "queued_at",
        header: "Encolada",
        meta: { label: "Encolada", hideOnMobile: true },
        cell: ({ row }) => (
          <span className="text-slate-600">{formatDateTime(row.original.queued_at)}</span>
        ),
      },
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Llamadas"
        description="Cada llamada con su estado, sus intentos y su línea de tiempo. Las llamadas en curso se refrescan solas."
        action={
          <Button variant="secondary" onClick={() => void lista.reload(true)}>
            Actualizar
          </Button>
        }
      />

      {/* Aplicar un filtro cambia la ruta, y el hook vuelve a la primera
          página al detectarlo: un cursor de la lista anterior no significa
          nada en la nueva. */}
      <FilterBar
        filters={filters}
        onApply={setFilters}
        onClear={() => setFilters(EMPTY_FILTERS)}
      />

      <p className="mb-3 flex items-center gap-2 text-xs text-slate-500">
        {canal === "en-vivo" ? (
          <>
            {/* Un punto que late: dice que lo que se ve llega solo, sin que
                nadie tenga que recargar. */}
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            En vivo: las llamadas aparecen según pasan.
          </>
        ) : lista.refrescando ? (
          "Actualizando…"
        ) : hasLiveCalls ? (
          `Hay llamadas en curso; se actualiza cada ${POLL_INTERVAL_MS / 1000} s.`
        ) : (
          `Se actualiza cada ${POLL_IDLE_MS / 1000} s, y cada ${POLL_INTERVAL_MS / 1000} s mientras haya llamadas en curso.`
        )}
      </p>

      <DataTable<Call>
        data={calls}
        columns={columnas}
        getRowId={(call) => call.id}
        loading={lista.loading}
        error={lista.error}
        onRetry={() => void lista.reload(false)}
        caption="Llamadas de la cuenta"
        empty={{
          title: "Sin llamadas",
          description: isFiltered(filters)
            ? "Ninguna llamada coincide con los filtros."
            : "Cuando se origine la primera llamada aparecerá aquí.",
        }}
        // La búsqueda y el orden son sobre la página cargada: el histórico
        // de llamadas no cabe en memoria, y los filtros que sí van al
        // servidor son los de arriba, que viajan en la query.
        search={{ placeholder: "Buscar por número o id…" }}
        hideableColumns
        urlStateKey="llamadas"
        onRowClick={(call) => navigate(`/calls/${call.id}`)}
        virtualizeFrom={200}
        pagination={{
          pageSize: lista.enEstaPagina,
          hasNext: lista.hayMas,
          hasPrevious: lista.hayAnterior,
          onNext: lista.siguiente,
          onPrevious: lista.anterior,
        }}
      />
    </div>
  );
}

function FilterBar({
  filters,
  onApply,
  onClear,
}: {
  filters: Filters;
  onApply: (filters: Filters) => void;
  onClear: () => void;
}) {
  const [draft, setDraft] = useState(filters);

  // Si los filtros se limpian desde fuera, el formulario se sincroniza.
  useEffect(() => setDraft(filters), [filters]);

  return (
    <Card className="mb-4 p-4">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          onApply(draft);
        }}
      >
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Estado</span>
          <select
            className={`${inputClass} mt-1`}
            value={draft.status}
            onChange={(e) => setDraft({ ...draft, status: e.target.value as CallStatus | "" })}
          >
            <option value="">Todos</option>
            {STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dirección</span>
          <select
            className={`${inputClass} mt-1`}
            value={draft.direction}
            onChange={(e) =>
              setDraft({ ...draft, direction: e.target.value as CallDirection | "" })
            }
          >
            <option value="">Todas</option>
            <option value="outbound">Saliente</option>
            <option value="inbound">Entrante</option>
          </select>
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Desde</span>
          <input
            type="datetime-local"
            className={`${inputClass} mt-1`}
            value={draft.from}
            onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          />
        </label>

        <label className="block">
          <span className="text-xs font-medium text-slate-600">Hasta</span>
          <input
            type="datetime-local"
            className={`${inputClass} mt-1`}
            value={draft.to}
            onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          />
        </label>

        <Button type="submit">Filtrar</Button>
        {isFiltered(filters) && (
          <Button type="button" variant="secondary" onClick={onClear}>
            Limpiar
          </Button>
        )}
      </form>
    </Card>
  );
}

// ------------------------------------------------------------- helpers -----

const STATUSES: CallStatus[] = [
  "queued", "initiated", "ringing", "in-progress",
  "completed", "busy", "no-answer", "failed", "canceled",
];

export const STATUS_LABELS: Record<CallStatus, string> = {
  queued: "En cola",
  initiated: "Originada",
  ringing: "Timbrando",
  "in-progress": "En curso",
  completed: "Completada",
  busy: "Ocupado",
  "no-answer": "Sin respuesta",
  failed: "Fallida",
  canceled: "Cancelada",
};

/** Un estado no terminal: puede cambiar, así que merece polling. */
export function isLive(status: CallStatus): boolean {
  return ["queued", "initiated", "ringing", "in-progress"].includes(status);
}

export function StatusBadge({ status }: { status: CallStatus }) {
  const tone =
    status === "completed" ? "good"
    : status === "failed" ? "bad"
    : status === "busy" || status === "no-answer" || status === "canceled" ? "warn"
    : "neutral";
  return <Badge tone={tone}>{STATUS_LABELS[status]}</Badge>;
}

function isFiltered(filters: Filters): boolean {
  return Object.values(filters).some((value) => value !== "");
}

/**
 * buildQuery arma el query string de los filtros.
 *
 * Las fechas van en RFC3339 UTC porque es lo que el endpoint parsea; el input
 * `datetime-local` da hora local sin zona, así que hay que convertir. Mandarlo
 * tal cual daría resultados desplazados por el huso y nadie sabría por qué.
 */
function buildQuery(filters: Filters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.direction) params.set("direction", filters.direction);
  if (filters.from) params.set("from", localToRFC3339(filters.from));
  if (filters.to) params.set("to", localToRFC3339(filters.to));
  const query = params.toString();
  return query ? `?${query}` : "";
}

function localToRFC3339(local: string): string {
  const date = new Date(local);
  return Number.isNaN(date.getTime()) ? local : date.toISOString();
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

/**
 * useInterval ejecuta algo cada `delay` ms; `null` lo detiene.
 *
 * El callback va en una ref para que cambiarlo no reinicie el temporizador:
 * sin eso, cada render reprograma el intervalo y el refresco acaba disparando
 * mucho más seguido de lo configurado.
 *
 * # Con la pestaña en segundo plano no hace nada
 *
 * Y al volver, refresca de inmediato en lugar de esperar al siguiente ciclo.
 * Son dos caras de lo mismo: una pestaña olvidada en otra ventana pidiendo
 * llamadas cada cinco segundos durante toda la tarde es tráfico que nadie
 * mira, y quien vuelve a ella quiere ver lo que pasa **ahora**, no lo que
 * pasaba cuando la dejó.
 */
export function useInterval(callback: () => void, delay: number | null): void {
  const saved = useRef(callback);
  useEffect(() => {
    saved.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delay === null) return;

    const id = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      saved.current();
    }, delay);

    function alVolver() {
      if (document.visibilityState === "visible") saved.current();
    }
    document.addEventListener("visibilitychange", alVolver);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", alVolver);
    };
  }, [delay]);
}
